import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import fetchApi from '@/utils/fetchApi'
import type { MergedUserListItem, ConfiguratorPagination } from 'shared/src'
import { computed, type MaybeRef, toValue } from 'vue'

export interface UsersListParams {
  q?: string
  role?: string[]
  status?: string[]
  page?: number
  limit?: number
}

export interface UsersListResult {
  users: MergedUserListItem[]
  pagination: ConfiguratorPagination
}

export const usersListQueryKey = (params: UsersListParams) => ['users', 'list', params] as const

export function useUsersListQuery(params: MaybeRef<UsersListParams>) {
  const queryKey = computed(() => usersListQueryKey(toValue(params)))

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = toValue(params)
      const query: Record<string, string | number | boolean> = {}

      if (p.page) query.page = p.page
      if (p.limit) query.limit = p.limit
      if (p.q) query.q = p.q
      if (p.role?.length) query.role = p.role.join(',')
      if (p.status?.length) query.status = p.status.join(',')

      const result = (await fetchApi<UsersListResult>({
        endpoint: '/api/users/list',
        query,
      })) as UsersListResult | null

      return result ?? defaultUsersListResult()
    },
    placeholderData: keepPreviousData,
  })
}

function defaultUsersListResult(): UsersListResult {
  return {
    users: [],
    pagination: {
      has_more: false,
      limit: 20,
      page: 1,
      processing_time_ms: 0,
      total_hits: 0,
      total_pages: 0,
    },
  }
}
